import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { MetricCard } from '../../components/ui/Card.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';
import { formatCurrency } from '../../utils/formatters.js';

import { DownPaymentModal } from './DownPaymentModal.jsx';
import { DownPaymentApplyModal } from './DownPaymentApplyModal.jsx';

const STATUS_VARIANT = { REQUESTED: 'warning', RECEIVED: 'info', APPLIED: 'success' };

export function DownPaymentsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [applyFor, setApplyFor] = useState(null);

  const list = useQuery({ queryKey: ['finance', 'down-payments'], queryFn: () => financeApi.listDownPayments().then((r) => r.items) });
  const rows = list.data || [];
  const count = (s) => rows.filter((r) => r.status === s).length;

  const receive = useMutation({
    mutationFn: (id) => financeApi.receiveDownPayment(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['finance', 'down-payments'] }),
  });

  const columns = [
    { key: 'number', label: t('dp.col.number'), render: (r) => <span className="mono">{r.dpNumber}</span> },
    { key: 'party', label: t('dp.col.party'), render: (r) => <>{r.partyName} <span className="muted text-sm">{t(`dp.partyType.${r.partyType}`)}</span></> },
    { key: 'amount', label: t('dp.col.amount'), align: 'right', render: (r) => <span className="mono num">{formatCurrency(r.amount)}</span> },
    { key: 'status', label: t('col.status'), render: (r) => <Badge variant={STATUS_VARIANT[r.status]}>{t(`dp.status.${r.status}`)}</Badge> },
    { key: 'actions', label: '', align: 'right', render: (r) => (
      <>
        {r.status === 'REQUESTED' && <Button size="sm" variant="ghost" disabled={receive.isPending} onClick={() => receive.mutate(r.id)}>{t('dp.action.receive')}</Button>}
        {r.status === 'RECEIVED' && <Button size="sm" variant="primary" onClick={() => setApplyFor(r)}>{t('dp.action.apply')}</Button>}
      </>
    ) },
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('dp.title')}</h1>
          <p className="muted">{t('dp.subtitle')}</p>
        </div>
        <div className="page-actions">
          <Button variant="primary" onClick={() => setCreateOpen(true)}>{t('dp.action.new')}</Button>
        </div>
      </header>

      <div className="card-grid">
        <MetricCard label={t('dp.status.REQUESTED')} value={String(count('REQUESTED'))} trendVariant={count('REQUESTED') > 0 ? 'warning' : 'success'} />
        <MetricCard label={t('dp.status.RECEIVED')} value={String(count('RECEIVED'))} />
        <MetricCard label={t('dp.status.APPLIED')} value={String(count('APPLIED'))} trendVariant="success" />
      </div>

      <Table columns={columns} rows={rows} isLoading={list.isLoading} emptyMessage={t('dp.empty')} />

      <DownPaymentModal open={createOpen} onClose={() => setCreateOpen(false)} />
      <DownPaymentApplyModal downPayment={applyFor} onClose={() => setApplyFor(null)} />
    </section>
  );
}
